import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Badge
} from "@chakra-ui/react";
import { FaTint, FaFlask, FaSun, FaArrowRight, FaCheckCircle } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterFiltrationHub: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/water-filtration"
        fallbackTitle="Water Filtration Systems for Australian Homes — One Water Australia"
        fallbackDescription="UV, carbon, reverse osmosis, whole-home — understand water filtration technology and find the right system for your home."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 16, md: 24 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1563453392212-326f5e854473?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" maxW="3xl" mx="auto">
            <Icon as={FaFlask} boxSize={12} color={WATER_BLUE} />
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Water filtration systems for Australian homes.
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.800">
              Understand the technology first. Choose the right approach for your water, your property, and your household.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0" }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Technology cards */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} mb={14} textAlign="center">
          <Heading size="xl" color={NAVY}>Filtration Technologies</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Each technology targets different contaminants. The right system depends on what's actually in your water.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {[
            {
              icon: FaSun,
              title: "UV Filtration",
              badge: "Biological",
              href: "/water/water-filtration/uv-system",
              img: "https://images.unsplash.com/photo-1581093577421-f561a654a353?auto=format&fit=crop&w=600&q=80",
              imgAlt: "UV water purification technology",
              desc: "Destroys bacteria, viruses, and parasites using ultraviolet light — without chemicals. Doesn't remove dissolved solids or sediment. Part of a multi-stage system.",
              best: ["Tank and rainwater systems", "Bore water", "Rural and older infrastructure", "Chemical-free preference"],
            },
            {
              icon: FaTint,
              title: "Carbon Filtration",
              badge: "Chemical",
              href: "/water/water-filters",
              img: "https://images.unsplash.com/photo-1504472478235-9bc48ba4d60f?auto=format&fit=crop&w=600&q=80",
              imgAlt: "Clean filtered water from kitchen tap",
              desc: "Activated carbon removes chlorine, chloramines, taste, odour, and some organic compounds. The workhorse of point-of-use and whole-home systems.",
              best: ["Chlorine and taste improvement", "Whole-home treatment", "As first stage in multi-stage systems", "Most Australian mains water"],
            },
            {
              icon: FaFlask,
              title: "Reverse Osmosis",
              badge: "Dissolved solids",
              href: "/water/blog/reverse-osmosis-guide",
              img: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?auto=format&fit=crop&w=600&q=80",
              imgAlt: "Reverse osmosis water purification system",
              desc: "Forces water through a 0.0001 micron membrane, removing dissolved solids, heavy metals, fluoride, nitrates, and sodium. Point-of-use, kitchen sink.",
              best: ["High TDS or bore water", "Heavy metal or nitrate concerns", "Fluoride removal", "Highest purity drinking water"],
            },
            {
              icon: FaTint,
              title: "Whole-Home Filtration",
              badge: "Whole property",
              href: "/water/water-filters",
              img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=600&q=80",
              imgAlt: "Australian home with whole-home water treatment",
              desc: "Point-of-entry system treating every tap, shower, and appliance. Typically sediment + carbon stages. Addresses chlorine at the source — not just the kitchen.",
              best: ["Skin and hair sensitivity to chlorine", "Appliance protection", "Every tap filtered", "Upstream protection for RO"],
            },
          ].map((tech) => (
            <Box
              key={tech.title}
              bg="white"
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ shadow: "lg", borderColor: WATER_BLUE, transform: "translateY(-2px)" }}
              transition="all 0.3s"
              overflow="hidden"
            >
              <Box h="200px" overflow="hidden" position="relative">
                <Box
                  as="img"
                  src={tech.img}
                  alt={tech.imgAlt}
                  w="full"
                  h="full"
                  objectFit="cover"
                />
                <Box position="absolute" top={3} right={3}>
                  <Badge bg={ICE_MIST} color={DEEP_AQUA} px={3} py={1} borderRadius="full" fontSize="xs" fontWeight="600">
                    {tech.badge}
                  </Badge>
                </Box>
              </Box>
              <Box p={8}>
                <HStack mb={4}>
                  <Icon as={tech.icon} boxSize={8} color={WATER_BLUE} />
                </HStack>
                <Heading size="md" color={NAVY} mb={3}>{tech.title}</Heading>
                <Text color="gray.600" mb={5} fontSize="sm">{tech.desc}</Text>
                <Text fontWeight="600" color={NAVY} fontSize="xs" mb={3}>BEST FOR:</Text>
                <VStack align="flex-start" spacing={1} mb={5}>
                  {tech.best.map((b) => (
                    <HStack key={b} spacing={2}>
                      <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={3} />
                      <Text fontSize="xs" color="gray.600">{b}</Text>
                    </HStack>
                  ))}
                </VStack>
                {tech.href && (
                  <Button
                    as={RouterLink}
                    to={tech.href}
                    variant="link"
                    color={DEEP_AQUA}
                    rightIcon={<FaArrowRight />}
                    fontSize="sm"
                    fontWeight="600"
                    _hover={{ color: NAVY }}
                  >
                    Learn more
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Choosing the right approach */}
      <Box bg={ICE_MIST} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={12} textAlign="center">
            <Heading size="xl" color={NAVY}>Choosing the Right Approach</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              We assess first, recommend second. Here's the general framework — but your water determines the right answer.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {[
              { scenario: "Chlorine taste, skin sensitivity, appliance wear", recommendation: "Whole-home carbon filtration", note: "Treats every tap in the house." },
              { scenario: "Best possible drinking water purity", recommendation: "Under-sink reverse osmosis", note: "Removes dissolved solids fluoride and metals." },
              { scenario: "Tank, rainwater, bore, or ageing infrastructure", recommendation: "UV + multi-stage system", note: "Biological protection beyond standard filtration." },
            ].map((item) => (
              <Box key={item.scenario} p={7} bg="white" borderRadius="xl" shadow="sm">
                <Text fontSize="xs" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider" mb={2}>Scenario</Text>
                <Text fontWeight="600" color={NAVY} mb={4} fontSize="sm">{item.scenario}</Text>
                <Text fontSize="xs" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider" mb={2}>Recommendation</Text>
                <Text fontWeight="700" color={NAVY} mb={2}>{item.recommendation}</Text>
                <Text fontSize="sm" color="gray.500">{item.note}</Text>
              </Box>
            ))}
          </SimpleGrid>
          <Box textAlign="center" mt={10}>
            <Text color="gray.600" mb={4}>Not sure what scenario applies to you?</Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={DEEP_AQUA}
              color="white"
              size="lg"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: NAVY }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Why One Water */}
      <Container maxW="container.xl" py={16}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
          <VStack align="flex-start" spacing={5}>
            <Heading size="lg" color={NAVY}>We assess first, recommend second.</Heading>
            <Text color="gray.600">
              Every home has different water. Every household has different needs. We don't lead with a product — we lead with understanding what your water actually contains, then specify the system that addresses it.
            </Text>
            <Text color="gray.600">
              One Water Australia is part of the One Group Prosperity Engine. Your water health is the foundation of health, efficiency, structure and growth.
            </Text>
          </VStack>
          <SimpleGrid columns={2} spacing={4}>
            {[
              { label: "Assessment First" },
              { label: "UNSW Lab Testing" },
              { label: "Professional Specification" },
              { label: "Long-Term Support" },
            ].map((item) => (
              <Box key={item.label} p={5} bg={ICE_MIST} borderRadius="lg" textAlign="center">
                <Text fontWeight="700" color={NAVY} fontSize="sm">{item.label}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </SimpleGrid>
      </Container>

      {/* Related */}
      <Box bg="gray.50" py={12}>
        <Container maxW="container.xl">
          <Heading size="md" color={NAVY} mb={6}>Related Reading</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {[
              { title: "UV Water Filtration System", href: "/water/water-filtration/uv-system", desc: "What UV does — and when you need it." },
              { title: "Reverse Osmosis Guide", href: "/water/blog/reverse-osmosis-guide", desc: "Honest pros and cons of RO in Australia." },
              { title: "RO vs Whole-Home Filtration", href: "/water/blog/reverse-osmosis-vs-whole-home", desc: "These aren't competing technologies." },
            ].map((link) => (
              <Box
                key={link.title}
                as={RouterLink}
                to={link.href}
                p={5}
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ borderColor: WATER_BLUE }}
                transition="all 0.2s"
                display="block"
                textDecoration="none"
              >
                <Text fontWeight="600" color={NAVY} mb={1} fontSize="sm">{link.title}</Text>
                <Text fontSize="sm" color="gray.600">{link.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterFiltrationHub;
